
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExternalLink, Loader2, Search } from "lucide-react";
import { Association, Property } from "@/models";
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import LocationPickerButton from '@/components/ui/location-picker-button';
import { US_STATES } from '@/utils/statesList';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface PropertyFormContentProps {
  property: Partial<Property>;
  setProperty: React.Dispatch<React.SetStateAction<Partial<Property>>>;
  associations: Association[];
  isLoading: boolean;
  onSave: () => void;
  showAssociationField?: boolean;
  showSuiteUnitFirst?: boolean;
}

const PropertyFormContent: React.FC<PropertyFormContentProps> = ({
  property,
  setProperty,
  associations = [],
  isLoading,
  onSave,
  showAssociationField = true,
  showSuiteUnitFirst = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProperty(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStateChange = (value: string) => {
    setProperty(prev => ({
      ...prev,
      state: value
    }));
  };

  const handleAssociationChange = (value: string) => {
    const associationId = parseInt(value);
    setProperty(prev => ({
      ...prev,
      association_id: associationId
    }));
    
    // Find the selected association
    const selectedAssociation = associations.find(a => a.id === associationId);
    
    // Prefill form fields with default values from the association
    if (selectedAssociation) {
      setProperty(prev => ({
        ...prev,
        association_id: associationId,
        address: selectedAssociation.default_property_address || prev.address || "",
        city: selectedAssociation.default_property_city || prev.city || "",
        state: selectedAssociation.default_property_state || prev.state || "",
        zip_code: selectedAssociation.default_property_zip || prev.zip_code || "",
        latitude: selectedAssociation.default_property_latitude || prev.latitude,
        longitude: selectedAssociation.default_property_longitude || prev.longitude,
      }));
    }
  };

  const handleMapLocationSelect = (latitude: number, longitude: number) => {
    setProperty(prev => ({
      ...prev,
      latitude,
      longitude
    }));
  };

  const handleAddressSelect = (addressData: {
    fullAddress: string;
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lon?: number;
  }) => {
    setProperty(prev => ({
      ...prev,
      address: addressData.fullAddress,
      city: addressData.city || prev.city,
      state: addressData.state || prev.state,
      zip_code: addressData.zipCode || prev.zip_code,
      latitude: addressData.lat || prev.latitude,
      longitude: addressData.lon || prev.longitude
    }));
  };

  const getGoogleMapsUrl = (lat?: number, lng?: number) => {
    if (!lat || !lng) return null;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  // Make sure filteredAssociations is always an array, even if associations is undefined
  const filteredAssociations = associations && searchValue
    ? associations.filter(association => 
        association.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : associations || [];
    
  // Effect to prefill property form with default association values when component mounts
  useEffect(() => {
    if (property.association_id && !property.address && associations.length > 0) {
      const selectedAssociation = associations.find(a => a.id === property.association_id);
      if (selectedAssociation) {
        setProperty(prev => ({
          ...prev,
          address: selectedAssociation.default_property_address || prev.address || "",
          city: selectedAssociation.default_property_city || prev.city || "",
          state: selectedAssociation.default_property_state || prev.state || "",
          zip_code: selectedAssociation.default_property_zip || prev.zip_code || "",
          latitude: selectedAssociation.default_property_latitude || prev.latitude,
          longitude: selectedAssociation.default_property_longitude || prev.longitude,
        }));
      }
    }
  }, [property.association_id, associations]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Property Information</CardTitle>
        <CardDescription>
          Enter the details for this property
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showSuiteUnitFirst && (
          <div className="space-y-2">
            <Label htmlFor="suite_unit">Suite / Unit</Label>
            <Input
              id="suite_unit"
              name="suite_unit"
              placeholder="Enter suite / unit"
              value={property.suite_unit || ""}
              onChange={handleInputChange}
            />
          </div>
        )}
        
        <div className={`grid grid-cols-1 ${showAssociationField ? 'md:grid-cols-2' : ''} gap-4`}>
          {showAssociationField && (
            <div className="space-y-2">
              <Label htmlFor="association_id">Association <span className="text-red-500">*</span></Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                  >
                    {property.association_id && associations && associations.length > 0
                      ? associations.find((association) => association.id === property.association_id)?.name || "Select association..."
                      : "Select association..."}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput 
                      placeholder="Search associations..." 
                      className="h-9" 
                      value={searchValue}
                      onValueChange={setSearchValue}
                    />
                    {!filteredAssociations || filteredAssociations.length === 0 ? (
                      <CommandEmpty>No associations available.</CommandEmpty>
                    ) : (
                      <>
                        <CommandEmpty>No association found.</CommandEmpty>
                        <CommandGroup className="max-h-[300px] overflow-y-auto">
                          {filteredAssociations.map((association) => (
                            <CommandItem
                              key={association.id}
                              value={association.id.toString()}
                              onSelect={(value) => {
                                handleAssociationChange(value);
                                setOpen(false);
                                setSearchValue("");
                              }}
                            >
                              {association.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="address">Address <span className="text-red-500">*</span></Label>
            <AddressAutocomplete
              id="address"
              name="address"
              placeholder="Enter address"
              value={property.address || ""}
              onChange={handleInputChange}
              onAddressSelect={handleAddressSelect}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Select 
              onValueChange={handleStateChange} 
              value={property.state || ""}
            >
              <SelectTrigger id="state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              name="city"
              placeholder="Enter city"
              value={property.city || ""}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zip_code">Zip Code</Label>
            <Input
              id="zip_code"
              name="zip_code"
              placeholder="Enter zip code"
              value={property.zip_code || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>
        
        {!showSuiteUnitFirst && (
          <div className="space-y-2">
            <Label htmlFor="suite_unit">Suite / Unit</Label>
            <Input
              id="suite_unit"
              name="suite_unit"
              placeholder="Enter suite / unit"
              value={property.suite_unit || ""}
              onChange={handleInputChange}
            />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="latitude">Latitude</Label>
            <div className="flex items-center gap-2">
              <Input
                id="latitude"
                name="latitude"
                type="number"
                placeholder="Enter latitude"
                value={property.latitude !== undefined ? property.latitude : ""}
                onChange={(e) => setProperty(prev => ({
                  ...prev,
                  latitude: e.target.value ? parseFloat(e.target.value) : undefined
                }))}
                className="w-full"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="longitude">Longitude</Label>
            <div className="flex items-center gap-2">
              <Input
                id="longitude"
                name="longitude"
                type="number" 
                placeholder="Enter longitude"
                value={property.longitude !== undefined ? property.longitude : ""}
                onChange={(e) => setProperty(prev => ({
                  ...prev,
                  longitude: e.target.value ? parseFloat(e.target.value) : undefined
                }))}
                className="w-1/2"
              />
              <LocationPickerButton
                latitude={property.latitude}
                longitude={property.longitude}
                onLocationSelect={handleMapLocationSelect}
              />
              {property.latitude !== undefined && property.longitude !== undefined && (
                <a
                  href={getGoogleMapsUrl(property.latitude, property.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80"
                  title="View on Google Maps"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          <Button onClick={onSave} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Property
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyFormContent;
